/**
 * BlogVideo - Video player component for blog posts
 *
 * Fetches video status from API and renders with loading/error states.
 * Videos autoplay muted and loop for GIF-like behavior.
 */

import { useState, useEffect } from 'react'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { publicFetch } from '@/services/api'

interface BlogVideoProps {
  postSlug: string
  videoId: string
  caption?: string
}

interface VideoStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed'
  video_url?: string
  error?: string
}

export default function BlogVideo({ postSlug, videoId, caption }: BlogVideoProps) {
  const [status, setStatus] = useState<VideoStatus>({ status: 'pending' })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval> | null = null

    const fetchStatus = async () => {
      try {
        const res = await publicFetch(`/api/blog/videos/${encodeURIComponent(postSlug)}`)
        if (!res.ok) throw new Error('Failed to fetch video status')

        const data = await res.json()
        const video = data.videos?.find((v: any) => v.video_id === videoId)

        if (video) {
          setStatus({
            status: video.status,
            video_url: video.video_url,
            error: video.error
          })

          // Stop polling if completed or failed
          if (video.status === 'completed' || video.status === 'failed') {
            if (pollInterval) {
              clearInterval(pollInterval)
              pollInterval = null
            }
          }
        } else {
          // Video not found - might not be generated yet
          setStatus({ status: 'pending' })
        }
      } catch (err) {
        console.error('Error fetching video status:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchStatus()

    // Poll every 3 seconds if not completed
    pollInterval = setInterval(() => {
      if (status.status !== 'completed' && status.status !== 'failed') {
        fetchStatus()
      }
    }, 3000)

    return () => {
      if (pollInterval) clearInterval(pollInterval)
    }
  }, [postSlug, videoId])

  // Loading state
  if (loading && status.status === 'pending') {
    return (
      <div style={containerStyle}>
        <div style={loadingBoxStyle}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
          <p style={loadingTextStyle}>Loading video...</p>
        </div>
        {caption && <p style={captionStyle}>{caption}</p>}
      </div>
    )
  }

  // Processing state
  if (status.status === 'pending' || status.status === 'processing') {
    return (
      <div style={containerStyle}>
        <div style={loadingBoxStyle}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
          <p style={loadingTextStyle}>
            {status.status === 'pending' ? 'Preparing video...' : 'Generating video...'}
          </p>
          <p style={subTextStyle}>This may take a moment</p>
        </div>
        {caption && <p style={captionStyle}>{caption}</p>}
      </div>
    )
  }

  // Error state
  if (status.status === 'failed') {
    return (
      <div style={containerStyle}>
        <div style={errorBoxStyle}>
          <AlertCircle size={32} style={{ color: '#ef4444' }} />
          <p style={errorTextStyle}>Video generation failed</p>
          {status.error && <p style={subTextStyle}>{status.error}</p>}
        </div>
        {caption && <p style={captionStyle}>{caption}</p>}
      </div>
    )
  }

  // Completed - show video
  if (status.status === 'completed' && status.video_url) {
    return (
      <div style={containerStyle}>
        <video
          src={status.video_url}
          autoPlay
          muted
          loop
          playsInline
          controls
          style={videoStyle}
        />
        {caption && <p style={captionStyle}>{caption}</p>}
      </div>
    )
  }

  // Fallback
  return null
}

// Styles
const containerStyle: React.CSSProperties = {
  margin: '2rem 0',
  borderRadius: '12px',
  overflow: 'hidden',
  background: '#fafafa',
  border: '1px solid #eaeaea',
}

const loadingBoxStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '3rem 2rem',
  minHeight: '200px',
  gap: '0.75rem',
}

const errorBoxStyle: React.CSSProperties = {
  ...loadingBoxStyle,
  background: 'rgba(239, 68, 68, 0.05)',
}

const loadingTextStyle: React.CSSProperties = {
  color: '#4f46e5',
  fontSize: '0.95rem',
  margin: 0,
}

const errorTextStyle: React.CSSProperties = {
  color: '#fca5a5',
  fontSize: '0.95rem',
  margin: 0,
}

const subTextStyle: React.CSSProperties = {
  color: '#6b6b6b',
  fontSize: '0.8rem',
  margin: 0,
}

const videoStyle: React.CSSProperties = {
  width: '100%',
  display: 'block',
  maxHeight: '400px',
  objectFit: 'cover',
}

const captionStyle: React.CSSProperties = {
  color: '#5f5f5f',
  fontSize: '0.85rem',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '0.75rem 1rem',
  margin: 0,
  borderTop: '1px solid #eaeaea',
}
